import { format, Locale, parseISO } from 'date-fns'
import { da, enUS, fi, fr, nb, sv } from 'date-fns/locale'
import { DateTime } from 'luxon'
import { userLanguage } from './language'

export function toLocaleDatePreserveNull(date: string | DateTime | null | undefined): DateTime | null {
  if (DateTime.isDateTime(date)) return date
  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : null
  return dateTime?.isValid ? dateTime : null
}

export function formatDateShortPreserveNull(
  dateTime: DateTime | string | null | undefined,
  locale?: Locale
): string | null {
  return formatDateTimePreserveNull(dateTime, locale, DateTime.DATE_SHORT)
}

export function formatDateTimePreserveNull(
  dateTime: DateTime | string | null | undefined,
  locale?: Locale,
  formatter?: Intl.DateTimeFormatOptions
): string | null {
  return (
    toLocaleDatePreserveNull(dateTime)
      ?.setLocale(locale?.code ?? userLanguage())
      ?.toLocaleString(formatter) ?? null
  )
}

export function getISOSplitDateAndTime(dateTime?: string | null, locale?: Locale) {
  if (!dateTime) return ['', '']
  const parsedDateTime = parseISO(dateTime)
  const date = format(parsedDateTime, 'yyyy-MM-dd', { locale })
  const time = format(parsedDateTime, 'HH:mm', { locale })
  return [date, time]
}

export const getCutoffTimeForBooking = (
  cutoffDayForOrdering: number | null | undefined,
  isoCutoffTimeForOrdering: string | null | undefined,
  pickupDate: string | null
): string | null | undefined => {
  const cutoffTimeAtPickupDate = pickupDate && isoCutoffTimeForOrdering && `${pickupDate}T${isoCutoffTimeForOrdering}`
  const cutoffTime = cutoffTimeAtPickupDate
    ? DateTime.fromISO(cutoffTimeAtPickupDate).plus({
        minutes: 1,
        days: cutoffDayForOrdering ?? 0
      })
    : DateTime.now().plus({ days: 1 })

  return DateTime.now() < cutoffTime ? null : isoCutoffTimeForOrdering
}

export function dateFormatYYYYMMDD(date: Date | number, locale: Locale): string {
  return format(date, 'yyyy-MM-dd', { locale })
}
export function dateLocale(lang: string): Locale {
  switch (lang) {
    case 'sv':
      return sv
    case 'nb':
    case 'nb-NO':
      return nb
    case 'fi':
      return fi
    case 'fr':
      return fr
    case 'dk':
      return da
    default:
      return enUS
  }
}

export function getLocaleDateFormat(locale: Locale): string {
  switch (locale) {
    case enUS:
      return 'MM/dd/yyyy'
    case fr:
      return 'dd/MM/yyyy'
    case fi:
    case nb:
      return 'dd.MM.yyyy'
    case da:
    case sv:
    default:
      return 'yyyy-MM-dd'
  }
}

// It is the minimum number of characters needed to represent a date in specific locale considering year part has 4 digits
export function getMinimumFormDateValueLength(locale: Locale): number {
  switch (locale) {
    case sv:
    case fr:
      return 10
    default:
      return 8
  }
}
